import { Color } from '@solid/shared/styles/colors';
import { Button } from '@solid/shared/ui';
import { useMutation } from '@tanstack/react-query';
import { ErrorMessage, Field, Formik } from 'formik';
import React, { useContext, useId } from 'react';
import { Yup } from '@solid/shared/services/validation.service';
import { environment } from '../../../environments/environment';
import { AuthContext } from '../../contexts/auth.context';
import * as userService from '../../services/user.service';
import * as Styled from './UserCompanyDetailsForm.styles';
import { PAYMENT_METHOD } from '@solid/shared';
import { Trans, useTranslation } from 'react-i18next';

interface UserCompanyDetailsFormProps {
  color?: string;
  displayTerms?: boolean;
  showSuccess?: boolean;
  buttonText?: string;
  additionalButton?: React.ReactNode;
  onComplete?: () => void;
}

const UserCompanyDetailsForm: React.FC<UserCompanyDetailsFormProps> = ({
  color = Color.Neutral_1000_Black,
  displayTerms = false,
  showSuccess = false,
  buttonText = '',
  additionalButton,
  onComplete,
}) => {
  const { auth, refetchAuthState } = useContext(AuthContext);
  const id = useId();
  const { t } = useTranslation();
  const {
    mutate: updateUser,
    error,
    isLoading,
    isSuccess,
  } = useMutation<void, Error, Yup.InferType<typeof userService.UserCompanyDetailsSchema>>(async data => {
    const payload = { ...data };
    if (payload.companyDifferentBillingAddress === false) {
      payload.companyBillingName = '';
      payload.companyBillingStreet = '';
      payload.companyBillingZipCode = '';
      payload.companyBillingCity = '';
      payload.companyBillingCountry = null;
    }

    // Remove terms checkbox from payload data, as it's client-side required
    delete (payload as any).terms;

    await userService.updateUser(auth.user.id, payload);
    refetchAuthState();
    if (onComplete) {
      onComplete();
    }
  });

  const emailChangeDisabled = auth.user.paymentMethod === PAYMENT_METHOD.PADDLE && auth.user.companyEmail?.length > 0;

  const errorMessageRender = key => {
    return <p className={'help is-error'}>{t(key)}</p>;
  };

  return (
    <Formik
      initialValues={
        {
          companyName: auth.user.companyName,
          companyStreet: auth.user.companyStreet,
          companyZipCode: auth.user.companyZipCode,
          companyCity: auth.user.companyCity,
          companyCountry: auth.user.companyCountry ?? '',
          companyEmail: auth.user.companyEmail,
          companyVatId: auth.user.companyVatId,
          companyBillingName: auth.user.companyBillingName,
          companyBillingStreet: auth.user.companyBillingStreet,
          companyBillingZipCode: auth.user.companyBillingZipCode,
          companyBillingCity: auth.user.companyBillingCity,
          companyBillingCountry: auth.user.companyBillingCountry ?? '',
          companyDifferentBillingAddress: auth.user.companyDifferentBillingAddress,
        } as Yup.InferType<typeof userService.UserCompanyDetailsSchema>
      }
      onSubmit={values => updateUser(values)}
      validationSchema={userService.UserCompanyDetailsSchema}>
      {({ values, isValidating }) => (
        <Styled.Form $color={color}>
          <div className='form-row'>
            <div className='form-field'>
              <label htmlFor='companyName'>{t('pages.settingsAccount.userCompanyDetailsForm.companyName')}</label>
              <div className='control'>
                <Field type='text' name='companyName' id='companyName' autoComplete='organization' required />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='companyName' render={errorMessageRender} />
            </div>

            <div className='form-field'>
              <label htmlFor='companyStreet'>{t('common.street')}</label>
              <div className='control'>
                <Field type='text' name='companyStreet' id='companyStreet' required />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='companyStreet' render={errorMessageRender} />
            </div>
          </div>

          <div className='form-row'>
            <div className='form-field'>
              <label htmlFor='companyCity'>{t('common.city')}</label>
              <div className='control'>
                <Field type='text' name='companyCity' id='companyCity' required />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='companyCity' render={errorMessageRender} />
            </div>
            <div className='form-field'>
              <label htmlFor='companyZipCode'>{t('common.zipCode')}</label>
              <div className='control'>
                <Field type='text' name='companyZipCode' id='companyZipCode' required />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='companyZipCode' render={errorMessageRender} />
            </div>
          </div>

          <div className='form-row'>
            <div className='form-field'>
              <label htmlFor='companyCountry'>{t('common.country')}</label>
              <div className='control'>
                <Field component='select' type='select' name='companyCountry' id='companyCountry' required>
                  <option value='' disabled>
                    {t('common.pleaseSelect')}
                  </option>
                  <option value='DE'>Deutschland</option>
                  <option value='AT'>Österreich</option>
                  <option value='CH'>Schweiz</option>
                </Field>
              </div>
              <ErrorMessage component='p' className='help is-danger' name='companyCountry' render={errorMessageRender} />
            </div>
            <div className='form-field is-ghost'></div>
          </div>

          <div className='form-row'>
            <div className='form-field'>
              <label htmlFor='companyVatId'>{t('pages.settingsAccount.userCompanyDetailsForm.companyVatId')}*</label>
              <div className='control'>
                <Field type='text' name='companyVatId' id='companyVatId' placeholder='DE123456789' required />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='companyVatId' render={errorMessageRender} />
            </div>
            <div className='form-field'>
              <label htmlFor='companyEmail'>{t('pages.settingsAccount.userCompanyDetailsForm.companyEmail')}*</label>
              <div className='control'>
                <Field
                  type='text'
                  name='companyEmail'
                  id='companyEmail'
                  placeholder='invoice@firma.de'
                  required
                  disabled={emailChangeDisabled}
                />
              </div>
              {emailChangeDisabled && (
                <p className='help'>
                  {t('pages.settingsAccount.userCompanyDetailsForm.emailChangeDisabled')}{' '}
                  <a href='mailto:info@solid-app.de'>info@solid-app.de</a>.
                </p>
              )}
              <ErrorMessage component='p' className='help is-danger' name='companyEmail' render={errorMessageRender} />
            </div>
          </div>

          <div className='form-row'>
            <Styled.DifferentBillingAddress className='form-field'>
              <label className='checkbox' htmlFor={`${id}-companyDifferentBillingAddress`}>
                <Field
                  type='checkbox'
                  id={`${id}-companyDifferentBillingAddress`}
                  name='companyDifferentBillingAddress'
                />
                <span>{t('pages.settingsAccount.userCompanyDetailsForm.companyDifferentBillingAddress')}</span>
              </label>
            </Styled.DifferentBillingAddress>
          </div>

          {values.companyDifferentBillingAddress && (
            <div>
              <div className='form-row'>
                <div className='form-field'>
                  <label htmlFor='companyBillingName'>{t('common.name')}*</label>
                  <div className='control'>
                    <Field type='text' name='companyBillingName' id='companyBillingName' autoComplete='organization' />
                  </div>
                  <ErrorMessage component='p' className='help is-danger' name='companyBillingName' render={errorMessageRender} />
                </div>

                <div className='form-field'>
                  <label htmlFor='companyBillingStreet'>{t('common.street')}*</label>
                  <div className='control'>
                    <Field type='text' name='companyBillingStreet' id='companyBillingStreet' />
                  </div>
                  <ErrorMessage component='p' className='help is-danger' name='companyBillingStreet' render={errorMessageRender} />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-field'>
                  <label htmlFor='companyBillingCity'>{t('common.city')}*</label>
                  <div className='control'>
                    <Field type='text' name='companyBillingCity' id='companyBillingCity' />
                  </div>
                  <ErrorMessage component='p' className='help is-danger' name='companyBillingCity' render={errorMessageRender} />
                </div>
                <div className='form-field'>
                  <label htmlFor='companyBillingZipCode'>{t('common.zipCode')}*</label>
                  <div className='control'>
                    <Field type='text' name='companyBillingZipCode' id='companyBillingZipCode' />
                  </div>
                  <ErrorMessage component='p' className='help is-danger' name='companyBillingZipCode' render={errorMessageRender} />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-field'>
                  <label htmlFor='companyBillingCountry'>{t('common.country')}*</label>
                  <div className='control'>
                    <Field component='select' type='select' name='companyBillingCountry' id='companyBillingCountry'>
                      <option value='' disabled>
                        {t('common.pleaseSelect')}
                      </option>
                      <option value='DE'>Deutschland</option>
                      <option value='AT'>Österreich</option>
                      <option value='CH'>Schweiz</option>
                    </Field>
                  </div>
                  <ErrorMessage component='p' className='help is-danger' name='companyBillingCountry' render={errorMessageRender} />
                </div>
                <div className='form-field is-ghost'></div>
              </div>
            </div>
          )}

          {displayTerms && (
            <div className='form-row'>
              <div className='form-field'>
                <label className='checkbox' htmlFor='terms'>
                  <Field type='checkbox' id='terms' name='terms' required />
                  <span>
                    <Trans
                      i18nKey='pages.settingsAccount.userCompanyDetailsForm.terms'
                      components={{
                        a: (
                          <a
                            href={`${environment.kirbyBaseUrl}/terms-of-use/plus`}
                            target='_blank'
                            rel='noopener noreferrer'
                          />
                        ),
                      }}
                    />
                  </span>
                </label>
              </div>
            </div>
          )}

          {isSuccess && showSuccess && (
            <div className='form-field'>
              <div className='notification is-success'>
                {t('pages.settingsAccount.userCompanyDetailsForm.successMessage')}
              </div>
            </div>
          )}
          {error && (
            <div className='form-field'>
              <div className='notification is-error'>{error.message}</div>
            </div>
          )}

          <Styled.BtnContainer className='form-field'>
            {additionalButton}
            <Button type='submit' isLoading={isLoading || isValidating} disabled={isLoading || isValidating}>
              {buttonText || t('common.save')}
            </Button>
          </Styled.BtnContainer>
        </Styled.Form>
      )}
    </Formik>
  );
};

export default UserCompanyDetailsForm;
